import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Edit,
  SimpleForm,
  useDataProvider,
  useNotify,
  useRedirect,
} from 'react-admin';
import {
  Typography,
  Box,
  Button,
  Grid,
  TextField as MuiTextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  CircularProgress,
  Snackbar,
  makeStyles,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import GetAppIcon from '@material-ui/icons/GetApp';
import InboxIcon from '@material-ui/icons/Inbox';
import FiberNewIcon from '@material-ui/icons/FiberNew';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import LockIcon from '@material-ui/icons/Lock';
import CloseIcon from '@material-ui/icons/Close';
import CameraAltIcon from '@material-ui/icons/CameraAlt';
import RemoveIcon from '@material-ui/icons/Remove';
import AddIcon from '@material-ui/icons/Add';
import { BrowserMultiFormatReader } from '@zxing/library';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Customer { id: number | string; code: string; name: string; phone: string; }
interface OrderData { id: number | string; orderNo: string; customerId: number | string; designCode: string | number; }
interface SalesBillData { id: number | string; billNo: string; orderId: number | string; }
interface PackedItem {
  id: number; dbId?: number | string; itemCode: string; productName: string;
  quantity: number; cartonNo: number; productId?: number | string;
  savedAt?: string; timestamp: Date; isExisting: boolean;
}
interface PendingItem { itemCode: string; productName: string; }

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtCarton = (n: number) => String(n).padStart(3, '0');

function resolveItemCode(item: any): string {
  return item.itemCode ?? item.item_code ?? item.code ?? item.barcode ?? item.sku ??
    item.product?.code ?? item.ProductCode ?? item.productCode ?? '';
}

function getItemCodeFromProduct(product: any): string {
  return product?.code ?? product?.itemCode ?? product?.Code ?? product?.barcode ?? product?.sku ?? '';
}

function exportCSV(bill: SalesBillData, order: OrderData | null, customer: Customer | null, capacity: number, items: PackedItem[]) {
  const rows = [
    ['Sales Packing Export'], [],
    ['Bill No.', bill.billNo], ['Sales Order No.', order?.orderNo ?? ''],
    ['Customer Code', customer?.code ?? ''], ['Customer Name', customer?.name ?? ''],
    ['Phone', customer?.phone ?? ''], ['Carton Capacity', String(capacity)], [],
    ['#', 'Item Code', 'Product Name', 'Qty', 'Carton No.', 'Saved At'],
    ...items.map((item, i) => [String(i + 1), item.itemCode || '—', item.productName || '',
      String(item.quantity), fmtCarton(item.cartonNo), item.savedAt ?? item.timestamp.toLocaleTimeString()]),
    [], ['Total Items', String(items.length)],
    ['Total Qty', String(items.reduce((s, x) => s + x.quantity, 0))],
    ['Total Cartons', String(items.length > 0 ? Math.max(...items.map(x => x.cartonNo)) : 0)],
  ];
  const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `SalesPacking_${bill.billNo.replace('/', '-')}.csv`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box' as const,
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    overflowX: 'hidden' as const,
  },

  // ── Header info card
  headerCard: {
    borderRadius: 12,
    marginBottom: 16,
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
  },
  headerCardTop: {
    background: 'linear-gradient(90deg, #1e293b 0%, #334155 100%)',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    [theme.breakpoints.down('xs')]: {
      padding: '8px 12px',
    },
  },
  headerCardTopTitle: {
    color: '#f1f5f9',
    fontWeight: 700,
    fontSize: '0.8rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase' as const,
  },
  headerCardBody: {
    padding: '12px 14px',
    background: '#fff',
    overflowX: 'hidden' as const,
    [theme.breakpoints.down('xs')]: {
      padding: '10px 10px',
    },
  },
  infoLabel: {
    fontSize: '0.7rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    color: '#94a3b8',
    marginBottom: 3,
  },
  infoValue: {
    fontWeight: 700,
    fontSize: '0.92rem',
    color: '#0f172a',
    fontFamily: 'monospace',
  },
  infoValueSub: {
    fontSize: '0.78rem',
    color: '#64748b',
    marginTop: 2,
  },

  // ── Carton status
  cartonCard: {
    borderRadius: 12,
    marginBottom: 16,
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
  },
  cartonCardBody: {
    padding: '16px 18px',
    background: '#fff',
  },
  cartonNum: {
    fontFamily: 'monospace',
    fontWeight: 900,
    fontSize: 32,
    lineHeight: 1,
    color: '#0f172a',
  },

  // ── Summary badges
  summaryBar: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap' as const,
    marginBottom: 14,
    padding: '10px 16px',
    background: 'linear-gradient(90deg, #1e293b 0%, #334155 100%)',
    borderRadius: '10px 10px 0 0',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: 'rgba(255,255,255,0.12)',
    borderRadius: 20,
    padding: '3px 12px',
    fontSize: '0.78rem',
    fontWeight: 600,
    color: '#f1f5f9',
  },
  badgeNum: {
    color: '#38bdf8',
    fontWeight: 800,
    fontSize: '0.9rem',
  },
  badgeGreen: {
    background: 'rgba(34,197,94,0.2)',
    color: '#86efac',
  },
  badgeBlue: {
    background: 'rgba(56,189,248,0.2)',
    color: '#38bdf8',
  },

  // ── Scan panel
  scanCard: {
    borderRadius: 12,
    marginBottom: 16,
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
  },
  scanCardBody: {
    padding: '12px 14px',
    background: '#fff',
    [theme.breakpoints.down('xs')]: {
      padding: '10px 10px',
    },
  },
  scanInput: {
    '& input': { fontFamily: 'monospace', fontSize: 14, letterSpacing: 1 },
  },
  cameraBtn: {
    height: 56,
    minWidth: 52,
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
    color: '#f1f5f9',
    borderRadius: 10,
    boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
    '&:hover': { background: '#1e293b' },
  },
  addBtn: {
    height: 56,
    fontWeight: 700,
    borderRadius: 10,
    fontSize: '0.9rem',
    textTransform: 'none' as const,
  },

  // ── Pending item box
  pendingBox: {
    marginTop: 12,
    padding: '12px 14px',
    borderRadius: 10,
    background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
    border: '2px solid #93c5fd',
    boxSizing: 'border-box' as const,
    width: '100%',
  },
  pendingCode: {
    fontFamily: 'monospace',
    fontWeight: 800,
    fontSize: 17,
    color: '#1d4ed8',
    letterSpacing: 1,
    marginBottom: 4,
    wordBreak: 'break-all' as const,
  },
  pendingName: {
    fontSize: '0.85rem',
    color: '#3b82f6',
    fontWeight: 500,
    marginBottom: 12,
  },
  qtyBtn: {
    width: 40,
    height: 40,
    borderRadius: 8,
    background: '#dbeafe',
    color: '#1d4ed8',
    border: '1.5px solid #93c5fd',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  qtyInput: {
    width: 100,
    '& input': { textAlign: 'center', fontFamily: 'monospace', fontWeight: 800, fontSize: 18 },
  },
  confirmBtn: {
    height: 40,
    fontWeight: 700,
    borderRadius: 8,
    textTransform: 'none' as const,
    fontSize: '0.88rem',
  },
  cancelBtn: {
    height: 40,
    fontWeight: 600,
    borderRadius: 8,
    textTransform: 'none' as const,
    fontSize: '0.88rem',
    border: '1.5px solid #94a3b8',
    color: '#475569',
  },
  hintText: {
    fontSize: '0.75rem',
    color: '#64748b',
    marginTop: 10,
    lineHeight: 1.6,
    borderTop: '1px solid #bfdbfe',
    paddingTop: 10,
  },

  // ── Items table
  tableWrapper: {
    overflowX: 'auto' as const,
    maxHeight: 440,
    overflowY: 'auto' as const,
    borderRadius: '0 0 12px 12px',
  },
  stickyHead: {
    position: 'sticky' as const,
    top: 0,
    backgroundColor: '#f8fafc',
    zIndex: 1,
    fontWeight: 700,
    fontSize: '0.7rem',
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    color: '#475569',
    borderBottom: '2px solid #e2e8f0',
    padding: '8px 10px',
    whiteSpace: 'nowrap' as const,
  },
  tableCell: {
    fontSize: '0.82rem',
    padding: '7px 10px',
    borderBottom: '1px solid #f1f5f9',
    verticalAlign: 'middle' as const,
    color: '#1e293b',
  },
  latestRow: { background: '#f0fdf4', borderLeft: '3px solid #22c55e' },
  existingRow: { background: '#fafafa' },
  newRow: { background: '#fff' },

  designChip: {
    display: 'inline-block',
    background: 'linear-gradient(135deg, #ede9fe, #ddd6fe)',
    color: '#5b21b6',
    borderRadius: 6,
    padding: '2px 8px',
    fontWeight: 700,
    fontSize: '0.72rem',
    letterSpacing: '0.04em',
    border: '1px solid #c4b5fd',
  },
  cartonChipStyle: {
    fontFamily: 'monospace',
    fontWeight: 700,
    background: '#1e293b',
    color: '#f1f5f9',
    borderRadius: 6,
    padding: '2px 10px',
    fontSize: '0.82rem',
  },

  // ── Empty state
  emptyBox: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px',
    color: '#94a3b8',
    gap: 8,
    background: '#fff',
    borderRadius: '0 0 12px 12px',
  },

  // ── Toolbar
  toolbarRow: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap' as const,
    padding: '12px 0 4px',
  },
  saveBtn: {
    height: 44,
    fontWeight: 700,
    borderRadius: 10,
    textTransform: 'none' as const,
    fontSize: '0.9rem',
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
    color: '#f1f5f9',
    boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
    '&:hover': { background: '#1e293b' },
  },

  // ── Table footer summary
  tableFooter: {
    padding: '10px 16px',
    background: '#f8fafc',
    borderTop: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'flex-end',
    borderRadius: '0 0 12px 12px',
  },

  // ── Scanner dialog
  scannerVideoBox: {
    width: '100%',
    borderRadius: 10,
    overflow: 'hidden',
    border: '2px solid #334155',
    background: '#000',
    position: 'relative' as const,
  },
  scannerOverlay: {
    position: 'absolute' as const,
    top: '50%', left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '70%', height: 80,
    border: '2px solid #38bdf8',
    borderRadius: 6,
    boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)',
    pointerEvents: 'none' as const,
  },

  '@keyframes pulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.3 },
  },
  pulseDot: {
    display: 'inline-block',
    width: 8, height: 8,
    borderRadius: '50%',
    backgroundColor: '#22c55e',
    marginRight: 8,
    verticalAlign: 'middle',
    animation: '$pulse 1.5s ease-in-out infinite',
  },
}));

// ─── Alert ────────────────────────────────────────────────────────────────────

const Alert = (props: any) => <MuiAlert elevation={6} variant="filled" {...props} />;

// ─── Info Field (mini display cell) ──────────────────────────────────────────

const InfoField: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => {
  const classes = useStyles();
  return (
    <div style={{ minWidth: 0 }}>
      <div className={classes.infoLabel}>{label}</div>
      <div className={classes.infoValue} style={{
        fontFamily: mono ? 'monospace' : 'inherit',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {value || <span style={{ color: '#cbd5e1' }}>—</span>}
      </div>
    </div>
  );
};

// ─── Carton Status ────────────────────────────────────────────────────────────

const CartonStatus: React.FC<{ cartonNo: number; filled: number; capacity: number }> = ({
  cartonNo, filled, capacity,
}) => {
  const classes = useStyles();
  const pct = capacity > 0 ? Math.round((filled / capacity) * 100) : 0;
  const isWarn = pct >= 70 && pct < 95;
  const isError = pct >= 95;
  const barColor = isError ? '#ef4444' : isWarn ? '#f97316' : '#22c55e';

  return (
    <Paper elevation={0} className={classes.cartonCard}>
      {/* top bar */}
      <div style={{
        background: 'linear-gradient(90deg, #1e293b 0%, #334155 100%)',
        padding: '10px 14px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 6,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <InboxIcon style={{ color: '#38bdf8', fontSize: 18 }} />
          <span style={{ color: '#f1f5f9', fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Active Carton
          </span>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <span style={{
            background: 'rgba(255,255,255,0.12)', borderRadius: 20,
            padding: '2px 10px', fontSize: '0.75rem', color: '#f1f5f9', fontWeight: 600,
          }}>
            {filled} / {capacity} units
          </span>
          <span style={{
            background: barColor, borderRadius: 20,
            padding: '2px 10px', fontSize: '0.75rem', color: '#fff', fontWeight: 700,
          }}>
            {pct}%
          </span>
        </div>
      </div>

      {/* body */}
      <div className={classes.cartonCardBody}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Typography variant="caption" style={{ color: '#94a3b8', display: 'block', marginBottom: 2 }}>
              Carton No.
            </Typography>
            <Typography className={classes.cartonNum}>{fmtCarton(cartonNo)}</Typography>
          </Grid>
          <Grid item xs>
            <div style={{ position: 'relative', marginTop: 4 }}>
              <div style={{
                height: 12, borderRadius: 6,
                background: '#f1f5f9', overflow: 'hidden',
              }}>
                <div style={{
                  height: '100%', width: `${pct}%`,
                  background: barColor, borderRadius: 6,
                  transition: 'width 0.5s ease, background-color 0.3s',
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                <Typography variant="caption" style={{ color: '#94a3b8', fontSize: '0.72rem' }}>
                  {filled} packed
                </Typography>
                <Typography variant="caption" style={{ color: '#94a3b8', fontSize: '0.72rem' }}>
                  {capacity - filled} remaining
                </Typography>
              </div>
            </div>

            {isError && (
              <Box mt={1}>
                <Alert severity="warning" style={{ padding: '2px 12px', borderRadius: 8, fontSize: '0.8rem' }}>
                  Carton almost full! Next item may start Carton <strong>{fmtCarton(cartonNo + 1)}</strong>
                </Alert>
              </Box>
            )}
          </Grid>
        </Grid>
      </div>
    </Paper>
  );
};

// ─── Pending Item Row ─────────────────────────────────────────────────────────

const PendingItemRow: React.FC<{
  itemCode: string; productName: string; qty: number; qtyError: string; verifying: boolean;
  onIncrement: () => void; onDecrement: () => void; onQtyChange: (v: string) => void;
  onConfirm: () => void; onCancel: () => void;
}> = ({ itemCode, productName, qty, qtyError, verifying, onIncrement, onDecrement, onQtyChange, onConfirm, onCancel }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  return (
    <Box className={classes.pendingBox} style={{ gridColumn: '1 / -1' }}>
      <div className={classes.pendingCode}>{itemCode}</div>
      {productName && <div className={classes.pendingName}>{productName}</div>}

      <Grid container spacing={1} alignItems="flex-start">
        <Grid item>
          <Grid container spacing={1} alignItems="center" wrap="nowrap">
            <Grid item>
              <Tooltip title="Decrease by 10">
                <span>
                  <button className={classes.qtyBtn} onClick={onDecrement} disabled={verifying}>
                    <RemoveCircleOutlineIcon style={{ fontSize: 18 }} />
                  </button>
                </span>
              </Tooltip>
            </Grid>
            <Grid item>
              <MuiTextField
                className={classes.qtyInput}
                variant="outlined" size="small" type="number"
                value={qty}
                onChange={e => onQtyChange(e.target.value)}
                error={!!qtyError}
                helperText={qtyError || 'Multiple of 10'}
                inputProps={{ min: 10, step: 10 }}
                disabled={verifying}
              />
            </Grid>
            <Grid item>
              <Tooltip title="Increase by 10">
                <span>
                  <button className={classes.qtyBtn} onClick={onIncrement} disabled={verifying}>
                    <AddCircleOutlineIcon style={{ fontSize: 18 }} />
                  </button>
                </span>
              </Tooltip>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm="auto">
          <Grid container spacing={1}>
            <Grid item xs={6} sm="auto">
              <Button
                fullWidth={isMobile}
                variant="contained" color="primary"
                startIcon={verifying ? <CircularProgress size={14} color="inherit" /> : <CheckCircleOutlineIcon />}
                onClick={onConfirm}
                disabled={!!qtyError || qty <= 0 || verifying}
                className={classes.confirmBtn}
              >
                {verifying ? 'Verifying…' : 'Confirm'}
              </Button>
            </Grid>
            <Grid item xs={6} sm="auto">
              <button className={classes.cancelBtn} onClick={onCancel} disabled={verifying}
                style={{ padding: '0 16px', cursor: 'pointer', background: 'none' }}>
                Cancel
              </button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <div className={classes.hintText}>
        💡 Qty starts at <strong>10</strong>. Press <strong>+</strong> to add 10 each time,
        or type any multiple of 10 (e.g. 210, 450, 940). Values like 205 are invalid.
      </div>
    </Box>
  );
};

// ─── Main Form ────────────────────────────────────────────────────────────────

const SalesPackingEditForm: React.FC<any> = (props) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const scanRef = useRef<HTMLInputElement>(null);

  // remote data
  const [salesBillData, setSalesBillData] = useState<SalesBillData | null>(null);
  const [customerData, setCustomerData] = useState<any | null>(null);
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [cartonCapacity, setCartonCapacity] = useState(200);
  const [resolvedDesignCode, setResolvedDesignCode] = useState<string>('');
  const [pageLoading, setPageLoading] = useState(false);

  // packing state
  const [scannedItems, setScannedItems] = useState<PackedItem[]>([]);
  const [scanCode, setScanCode] = useState('');
  const [pendingItem, setPendingItem] = useState<PendingItem | null>(null);
  const [pendingQty, setPendingQty] = useState(10);
  const [qtyError, setQtyError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [saving, setSaving] = useState(false);

  // scanner state
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerError, setScannerError] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const codeReaderRef = useRef<BrowserMultiFormatReader | null>(null);

  // snackbar
  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' | 'warning' | 'info' }>
    ({ open: false, msg: '', sev: 'success' });
  const showSnack = (msg: string, sev: 'success' | 'error' | 'warning' | 'info' = 'success') =>
    setSnack({ open: true, msg, sev });

  // derived
  const totalQty = scannedItems.reduce((s, x) => s + x.quantity, 0);
  const currentCartonNo = totalQty === 0 ? 1 : totalQty % cartonCapacity === 0
    ? totalQty / cartonCapacity : Math.floor(totalQty / cartonCapacity) + 1;
  const cartonFilled = totalQty % cartonCapacity === 0 && totalQty > 0
    ? cartonCapacity : totalQty % cartonCapacity;
  const totalCartons = scannedItems.length > 0 ? Math.max(...scannedItems.map(x => x.cartonNo)) : 0;
  const existingCount = scannedItems.filter(x => x.isExisting).length;
  const newCount = scannedItems.filter(x => !x.isExisting).length;

  useEffect(() => { if (props.id) fetchAll(props.id); }, [props.id]);
  useEffect(() => {
    if (!pageLoading && salesBillData) setTimeout(() => scanRef.current?.focus(), 200);
  }, [pageLoading, salesBillData]);

  const fetchAll = async (id: string) => {
    setPageLoading(true);
    let bill: any = null; let order: any = null;
    try {
      const { data: billData } = await dataProvider.getOne('sales-bills', { id });
      bill = billData; setSalesBillData(bill);

      let resolvedCapacity = 200;
      if (bill.orderId) {
        try {
          const { data: orderData } = await dataProvider.getOne('orders', { id: bill.orderId });
          order = orderData; setOrderData(order);
        } catch { }

        if (order?.customerId) {
          try {
            const { data: cust } = await dataProvider.getOne('customers', { id: order.customerId });
            setCustomerData(cust);
          } catch { }
        }

        if (order?.designCode) {
          try {
            const designCodeVal = order.designCode.toString();
            const isNumeric = /^\d+$/.test(designCodeVal);

            let group: any = null;

            if (isNumeric) {
              // designCode is likely a FK id — fetch by id first
              try {
                const { data: g } = await dataProvider.getOne('product-groups', { id: Number(designCodeVal) });
                group = g;
              } catch {
                // fallback: try filtering by code field
                const { data: groups } = await dataProvider.getList('product-groups', {
                  filter: { code: designCodeVal },
                  pagination: { page: 1, perPage: 1 }, sort: { field: 'id', order: 'ASC' },
                });
                if (groups.length > 0) group = groups[0];
              }
            } else {
              // designCode is already a string code
              const { data: groups } = await dataProvider.getList('product-groups', {
                filter: { code: designCodeVal },
                pagination: { page: 1, perPage: 1 }, sort: { field: 'id', order: 'ASC' },
              });
              if (groups.length > 0) group = groups[0];
            }

            if (group) {
              if (group.capacity) resolvedCapacity = group.capacity;
              // store the human-readable code (not the numeric id)
              setResolvedDesignCode(group.code ?? group.designCode ?? group.name ?? designCodeVal);
            } else {
              setResolvedDesignCode(designCodeVal);
            }
          } catch {
            setResolvedDesignCode(order.designCode.toString());
          }
        }
        setCartonCapacity(resolvedCapacity);
      }

      try {
        const { data: existingItems } = await dataProvider.getList('sales-bill-items', {
          filter: { salesBillId: parseInt(id) },
          pagination: { page: 1, perPage: 1000 }, sort: { field: 'id', order: 'ASC' },
        });

        if (existingItems && existingItems.length > 0) {
          const productIds = Array.from(new Set(existingItems.map((item: any) => item.productId).filter(Boolean)));
          let productsMap: { [key: string]: any } = {};

          if (productIds.length > 0) {
            try {
              const { data: products } = await dataProvider.getList('products', {
                filter: productIds.length === 1 ? { id: productIds[0] } : { id: { in: productIds } },
                pagination: { page: 1, perPage: 1000 }, sort: { field: 'id', order: 'ASC' },
              });
              productsMap = products.reduce((map: any, product: any) => { map[product.id] = product; return map; }, {});
            } catch { }
          }

          const sorted = [...existingItems].sort((a: any, b: any) => {
            if (a.cartonNo !== b.cartonNo) return (a.cartonNo ?? 1) - (b.cartonNo ?? 1);
            return (a.id ?? 0) - (b.id ?? 0);
          });

          let runningTotal = 0;
          const mapped: PackedItem[] = sorted.map((item: any) => {
            const qty = Number(item.qty ?? item.quantity ?? 0);
            const product = productsMap[item.productId];
            const itemCode = product ? getItemCodeFromProduct(product) : resolveItemCode(item);
            const productName = product?.productName || '';
            let cartonNo = Number(item.cartonNo ?? 0);
            if (!cartonNo || cartonNo < 1) {
              cartonNo = runningTotal % resolvedCapacity === 0 && runningTotal > 0
                ? runningTotal / resolvedCapacity : Math.floor(runningTotal / resolvedCapacity) + 1;
            }
            runningTotal += qty;
            return {
              id: -(item.id ?? Math.random()), dbId: item.id, itemCode, productName,
              quantity: qty, cartonNo, productId: item.productId,
              savedAt: item.createdAt ?? item.updatedAt ?? item.created_at ?? undefined,
              timestamp: item.createdAt ? new Date(item.createdAt) : item.created_at ? new Date(item.created_at) : new Date(),
              isExisting: true,
            };
          });

          setScannedItems(mapped.reverse());
        } else {
          setScannedItems([]);
        }
      } catch (itemErr) {
        setScannedItems([]);
        showSnack('Could not load existing items — starting fresh', 'warning');
      }
    } catch (err) {
      // non-fatal
    } finally {
      setPageLoading(false);
    }
  };

  const handleScanKey = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    const code = scanCode.trim().toUpperCase();
    if (!code) return;
    if (pendingItem) { showSnack('Confirm or cancel current item first', 'warning'); return; }
    validateAndAddItem(code);
  };

  const incrementQty = () => { setPendingQty(q => q + 10); setQtyError(''); };
  const decrementQty = () => { setPendingQty(q => q - 10 >= 10 ? q - 10 : 10); setQtyError(''); };
  const handleQtyChange = (val: string) => {
    const n = parseInt(val);
    if (isNaN(n) || n <= 0) { setPendingQty(0); setQtyError('Enter a valid quantity'); return; }
    setPendingQty(n);
    setQtyError(n % 10 !== 0 ? 'Qty must be a multiple of 10' : '');
  };

  const validateProductAgainstOrderItems = useCallback(async (productId: any | string) => {
    const orderId = orderData?.id;
    if (!orderId) {
      showSnack('Order details missing. Please refresh and try again.', 'error');
      return false;
    }

    const { data: orderItems } = await dataProvider.getList('order-items', {
      filter: { orderId, productId },
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'id', order: 'ASC' },
    });

    if (!orderItems || orderItems.length === 0) {
      showSnack('This product is not part of the selected order', 'error');
      return false;
    }

    return true;
  }, [orderData, dataProvider]);

  const validateAndAddItem = useCallback(async (itemCode: string) => {
    if (pendingItem) { showSnack('Confirm or cancel current item first', 'warning'); return; }
    setVerifying(true);
    let productName = '';
    try {
      const { data: products } = await dataProvider.getList('products', {
        filter: { code: itemCode, designCode: orderData?.designCode?.toString() ?? '' },
        pagination: { page: 1, perPage: 1 }, sort: { field: 'id', order: 'ASC' },
      });
      if (products.length === 0) { showSnack(`Item "${itemCode}" not found for this design code`, 'error'); return; }
      const productId = products[0].id;
      const isAllowedForOrder = await validateProductAgainstOrderItems(productId);
      if (!isAllowedForOrder) return;
      productName = products[0].productName || '';
    } catch {
      showSnack('Error validating item', 'error');
      return;
    } finally { setVerifying(false); }
    setPendingItem({ itemCode, productName });
    setPendingQty(10); setQtyError(''); setScanCode('');
  }, [pendingItem, orderData, dataProvider, validateProductAgainstOrderItems]);

  const commitItem = async () => {
    if (!pendingItem) return;
    if (pendingQty <= 0 || pendingQty % 10 !== 0) { showSnack('Qty must be a positive multiple of 10', 'error'); return; }

    // Capture these synchronously before any async work — pendingItem may be cleared
    const capturedItemCode = pendingItem.itemCode;
    const capturedProductName = pendingItem.productName;
    const capturedQty = pendingQty;

    setVerifying(true);
    let productId: number | string | undefined;
    try {
      const { data: products } = await dataProvider.getList('products', {
        filter: { code: capturedItemCode, designCode: orderData?.designCode?.toString() ?? '' },
        pagination: { page: 1, perPage: 1 }, sort: { field: 'id', order: 'ASC' },
      });
      if (products.length === 0) { showSnack(`Item "${capturedItemCode}" not found`, 'error'); return; }
      productId = products[0].id;
      const isAllowedForOrder = await validateProductAgainstOrderItems(productId);
      if (!isAllowedForOrder) return;
      // Also use fresh productName from API if pending one was empty
      const freshName = products[0].productName || capturedProductName;

      setScannedItems(prev => {
        const currentTotal = prev.reduce((s, x) => s + x.quantity, 0);
        const newEntries: PackedItem[] = [];
        let remaining = capturedQty; let runningTotal = currentTotal;
        while (remaining > 0) {
          const currentCartonFilled = runningTotal % cartonCapacity;
          const isCurrentCartonFull = currentCartonFilled === 0 && runningTotal > 0;
          const cNo = Math.floor(runningTotal / cartonCapacity) + 1;
          const filledInCarton = isCurrentCartonFull ? 0 : currentCartonFilled;
          const space = cartonCapacity - filledInCarton;
          const take = Math.min(remaining, space);
          newEntries.push({
            id: Date.now() + Math.random(), cartonNo: cNo,
            itemCode: capturedItemCode,
            productName: freshName,   // ← always populated now
            quantity: take, isExisting: false, savedAt: undefined,
            timestamp: new Date(), productId,
          });
          remaining -= take; runningTotal += take;
        }
        return [...prev, ...newEntries];
      });

      setPendingItem(null); setPendingQty(10); setQtyError(''); setScanCode('');
      showSnack(`Added ${capturedQty} units of ${capturedItemCode}`, 'success');
    } catch { showSnack('Error verifying item', 'error'); }
    finally { setVerifying(false); }
  };

  const startScanner = useCallback(async () => {
    try {
      setScannerError(''); setIsScanning(true);
      if (!codeReaderRef.current) codeReaderRef.current = new BrowserMultiFormatReader();
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } } });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1280 }, height: { ideal: 720 } } });
      }
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => { videoRef.current?.play(); };
      }
      if (videoRef.current) {
        codeReaderRef.current.decodeFromVideoElement(videoRef.current).then((result) => {
          if (result) {
            const scannedText = result.getText();
            if (scannedText) { validateAndAddItem(scannedText.trim().toUpperCase()); stopScanner(); }
          }
        }).catch(() => { setScannerError('Failed to decode barcode. Please try again.'); setIsScanning(false); });
      }
    } catch { setScannerError('Failed to access camera. Please ensure camera permissions are granted.'); setIsScanning(false); }
  }, [validateAndAddItem]);

  const stopScanner = useCallback(() => {
    if (codeReaderRef.current) codeReaderRef.current.reset();
    setIsScanning(false); setScannerOpen(false);
  }, []);

  useEffect(() => {
    if (scannerOpen && !isScanning) startScanner();
    return () => { if (codeReaderRef.current) codeReaderRef.current.reset(); };
  }, [scannerOpen, isScanning, startScanner]);

  const handleQuantityChange = (itemId: number, newQty: number) => {
    if (isNaN(newQty) || newQty <= 0 || newQty % 10 !== 0) { showSnack('Quantity must be a positive multiple of 10', 'warning'); return; }
    setScannedItems(prev => prev.map(item => item.id === itemId ? { ...item, quantity: newQty } : item));
  };

  const handleRemoveItem = (itemId: number) => {
    setScannedItems(prev => prev.filter(item => item.id !== itemId));
  };

  const handleSave = async () => {
    if (pendingItem) { showSnack('Confirm or cancel current item before saving', 'warning'); return; }
    if (scannedItems.length === 0) { showSnack('No items scanned yet', 'error'); return; }
    if (!salesBillData) { showSnack('Sales bill data missing', 'error'); return; }
    const invalidItems = scannedItems.filter(item => !item.productId);
    if (invalidItems.length > 0) { showSnack(`Some items missing product ID: ${invalidItems.map(i => i.itemCode).join(', ')}`, 'error'); return; }

    setSaving(true);
    try {
      const salesBillId = parseInt(props.id);
      try {
        const { data: existing } = await dataProvider.getList('sales-bill-items', {
          filter: { salesBillId }, pagination: { page: 1, perPage: 1000 }, sort: { field: 'id', order: 'ASC' },
        });
        await Promise.all(existing.filter((row: any) => row.id != null).map((row: any) =>
          dataProvider.delete('sales-bill-items', { id: row.id, previousData: row })));
      } catch { }

      const itemsToSave = [...scannedItems].reverse();
      for (const item of itemsToSave) {
        await dataProvider.create('sales-bill-items', {
          data: { salesBillId, productId: item.productId, qty: item.quantity, cartonNo: item.cartonNo },
        });
      }
      exportCSV(salesBillData, orderData, customerData, cartonCapacity, scannedItems);
      notify('Packing saved successfully', 'success');
      redirect('/sales-bills');
    } catch (err) {
      const anyErr: any = err;
      notify(`Error saving packing data: ${anyErr?.message || 'Unknown error'}`, 'error');
    } finally { setSaving(false); }
  };

  if (pageLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300} style={{ gap: 16 }}>
        <CircularProgress />
        <Typography color="textSecondary">Loading packing data…</Typography>
      </Box>
    );
  }

  return (
    <div style={{
      maxWidth: '100vw',
      overflowX: 'hidden',
      boxSizing: 'border-box',
    }}>
    <>
      <SimpleForm
        {...props}
        save={handleSave}
        toolbar={
          <Box className={classes.toolbarRow} px={2} pb={2}>
            <Button
              variant="contained"
              className={classes.saveBtn}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <GetAppIcon />}
              onClick={handleSave}
              disabled={saving || scannedItems.length === 0}
            >
              {saving ? 'Saving…' : 'Save & Export Excel'}
            </Button>
          </Box>
        }
      >
        <Box className={classes.root}>

          {/* ══ HEADER INFO ══════════════════════════════════════════════════ */}
          <Paper elevation={0} className={classes.headerCard}>
            <div className={classes.headerCardTop}>
              <LockIcon style={{ color: '#38bdf8', fontSize: 16 }} />
              <span className={classes.headerCardTopTitle}>Sales Packing Information</span>
            </div>
            <div className={classes.headerCardBody}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={2}>
                  <InfoField label="Bill No." value={salesBillData?.billNo ?? ''} mono />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <InfoField label="Order No." value={orderData?.orderNo ?? ''} mono />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <div>
                    <div className={classes.infoLabel}>Design Code</div>
                    {resolvedDesignCode
                      ? <span style={{
                          display: 'inline-block',
                          background: 'linear-gradient(135deg, #ede9fe, #ddd6fe)',
                          color: '#5b21b6', borderRadius: 6, padding: '3px 10px',
                          fontWeight: 700, fontSize: '0.82rem', letterSpacing: '0.04em',
                          border: '1px solid #c4b5fd', marginTop: 2,
                        }}>
                          {resolvedDesignCode}
                        </span>
                      : <span style={{ color: '#cbd5e1' }}>—</span>}
                  </div>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <InfoField label="Customer Name" value={customerData?.customerName ?? ''} />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <InfoField label="Mobile No." value={customerData?.mobileNo ?? ''} mono />
                </Grid>
                <Grid item xs={6} sm={1}>
                  <div>
                    <div className={classes.infoLabel}>Cap.</div>
                    <div className={classes.infoValue}>{cartonCapacity}</div>
                    <div className={classes.infoValueSub}>units</div>
                  </div>
                </Grid>
              </Grid>
            </div>
          </Paper>

          {/* ══ CARTON STATUS ════════════════════════════════════════════════ */}
          <CartonStatus cartonNo={currentCartonNo} filled={cartonFilled} capacity={cartonCapacity} />

          {/* ══ SCAN INTERFACE ════════════════════════════════════════════════ */}
          <Paper elevation={0} className={classes.scanCard}>
            {/* top bar doubles as summary when items exist */}
            <div style={{
              background: 'linear-gradient(90deg, #1e293b 0%, #334155 100%)',
              padding: '10px 14px',
              borderRadius: scannedItems.length > 0 ? '12px 12px 0 0' : undefined,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 6,
            }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span className={classes.badge}>
                  Cartons &nbsp;<span className={classes.badgeNum}>{totalCartons}</span>
                </span>
                <span className={classes.badge}>
                  Total Qty &nbsp;<span className={classes.badgeNum}>{totalQty}</span>
                </span>
                {existingCount > 0 && (
                  <span className={`${classes.badge} ${classes.badgeGreen}`}>
                    {existingCount} from DB
                  </span>
                )}
                {newCount > 0 && (
                  <span className={`${classes.badge} ${classes.badgeBlue}`}>
                    <span className={classes.pulseDot} /> {newCount} new
                  </span>
                )}
              </div>
              <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>Sales Packing</span>
            </div>

            <div className={classes.scanCardBody}>
              {/* scan input row — input + camera always on one line, Add Item full-width below */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {/* Row 1: text input + camera button (never wraps) */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <MuiTextField
                    inputRef={scanRef}
                    fullWidth variant="outlined" size="medium"
                    label="Item Code"
                    placeholder="Scan or type item code → press Enter"
                    value={scanCode}
                    disabled={!!pendingItem}
                    onChange={e => setScanCode(e.target.value.toUpperCase())}
                    onKeyDown={handleScanKey}
                    autoComplete="off"
                    className={classes.scanInput}
                    helperText="Press Enter after scanning"
                  />
                  <Button
                    variant="contained"
                    className={classes.cameraBtn}
                    onClick={() => setScannerOpen(true)}
                    disabled={!!pendingItem}
                    style={{ flexShrink: 0, height: 56, minWidth: 52 }}
                  >
                    <CameraAltIcon />
                  </Button>
                </div>

                {/* Row 2: Add Item full width */}
                <Button
                  fullWidth variant="contained" color="primary"
                  size="large"
                  disabled={!scanCode.trim() || !!pendingItem}
                  onClick={() => {
                    const code = scanCode.trim().toUpperCase();
                    if (!code || pendingItem) return;
                    validateAndAddItem(code);
                  }}
                  className={classes.addBtn}
                  style={{ height: 48 }}
                >
                  Add Item
                </Button>
              </div>

              {/* pending item */}
              {pendingItem && (
                <PendingItemRow
                  itemCode={pendingItem.itemCode}
                  productName={pendingItem.productName}
                  qty={pendingQty} qtyError={qtyError} verifying={verifying}
                  onIncrement={incrementQty} onDecrement={decrementQty}
                  onQtyChange={handleQtyChange} onConfirm={commitItem}
                  onCancel={() => { setPendingItem(null); setPendingQty(10); setQtyError(''); }}
                />
              )}
            </div>

            {/* items table / empty */}
            {scannedItems.length === 0 ? (
              <div className={classes.emptyBox}>
                <InboxIcon style={{ fontSize: 48, opacity: 0.2 }} />
                <Typography variant="body2">No items scanned yet. Scan a barcode above to begin.</Typography>
              </div>
            ) : (
              <>
                <div className={classes.tableWrapper}>
                  <Table size="small" style={{ background: '#fff', minWidth: 380 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.stickyHead} style={{ width: 36 }}>#</TableCell>
                        <TableCell className={classes.stickyHead}>Design / Item</TableCell>
                        <TableCell className={classes.stickyHead} align="center" style={{ width: 90 }}>Carton</TableCell>
                        <TableCell className={classes.stickyHead} align="center" style={{ width: 160 }}>Quantity</TableCell>
                        <TableCell className={classes.stickyHead} align="center" style={{ width: 70 }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scannedItems.map((item, idx) => {
                        const isLatest = idx === 0 && !item.isExisting;
                        return (
                          <TableRow
                            key={item.id}
                            hover
                            className={isLatest ? classes.latestRow : item.isExisting ? classes.existingRow : classes.newRow}
                            style={{ borderLeft: isLatest ? '3px solid #22c55e' : item.isExisting ? '3px solid #a3e635' : '3px solid transparent' }}
                          >
                            <TableCell className={classes.tableCell} style={{ color: '#94a3b8', fontSize: '0.75rem' }}>
                              {scannedItems.length - idx}
                            </TableCell>
                            {/* Merged Design + Item Code / Name */}
                            <TableCell className={classes.tableCell}>
                              {resolvedDesignCode && (
                                <span className={classes.designChip} style={{ marginRight: 8 }}>
                                  {resolvedDesignCode}
                                </span>
                              )}
                              <span style={{ fontWeight: 700, fontFamily: 'monospace', fontSize: '0.88rem', color: '#0f172a' }}>
                                {item.itemCode || <span style={{ color: '#cbd5e1' }}>No item code</span>}
                              </span>
                              {item.productName && (
                                <span style={{ color: '#64748b', fontSize: '0.82rem' }}> — {item.productName}</span>
                              )}
                              {item.isExisting && (
                                <span style={{
                                  display: 'inline-block', marginLeft: 8,
                                  fontSize: '0.68rem', background: '#dcfce7', color: '#15803d',
                                  borderRadius: 4, padding: '1px 6px', fontWeight: 600,
                                }}>
                                  saved
                                </span>
                              )}
                            </TableCell>
                            <TableCell className={classes.tableCell} align="center">
                              <span className={classes.cartonChipStyle}>{fmtCarton(item.cartonNo)}</span>
                            </TableCell>
                            <TableCell className={classes.tableCell} align="center">
                              <Box display="flex" alignItems="center" justifyContent="center">
                                <IconButton size="small" onClick={() => handleQuantityChange(item.id, item.quantity - 10)}
                                  disabled={item.quantity <= 10} style={{ marginRight: 4 }}>
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                                <Typography variant="body2" style={{ minWidth: 44, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 }}>
                                  {item.quantity}
                                </Typography>
                                <IconButton size="small" onClick={() => handleQuantityChange(item.id, item.quantity + 10)} style={{ marginLeft: 4 }}>
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </TableCell>
                            <TableCell className={classes.tableCell} align="center">
                              <Tooltip title="Remove item">
                                <IconButton size="small" onClick={() => handleRemoveItem(item.id)}
                                  style={{ color: '#ef4444', background: '#fee2e2', borderRadius: 6, padding: 4 }}>
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                <div className={classes.tableFooter}>
                  <Typography variant="caption" color="textSecondary">
                    Total: <strong>{totalQty} units</strong> across{' '}
                    <strong>{totalCartons} carton{totalCartons !== 1 ? 's' : ''}</strong>
                  </Typography>
                </div>
              </>
            )}
          </Paper>

        </Box>
      </SimpleForm>

      {/* Snackbar */}
      <Snackbar
        open={snack.open} autoHideDuration={2500}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>

      {/* Scanner Dialog */}
      <Dialog
        open={scannerOpen} onClose={stopScanner}
        maxWidth="sm" fullWidth fullScreen={isMobile}
        PaperProps={{ style: { borderRadius: isMobile ? 0 : 16, overflow: 'hidden' } }}
      >
        <DialogTitle disableTypography style={{
          background: 'linear-gradient(90deg, #1e293b 0%, #334155 100%)',
          color: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 20px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <CameraAltIcon style={{ fontSize: 22, color: '#38bdf8' }} />
            <Typography variant="h6" style={{ color: '#f1f5f9', fontSize: '1rem', fontWeight: 700 }}>
              Scan Barcode
            </Typography>
          </div>
          <IconButton onClick={stopScanner} size="small" style={{ color: '#94a3b8' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent style={{ padding: 20, background: '#0f172a' }}>
          {scannerError ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={300} padding={2}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📵</div>
              <Typography style={{ color: '#f87171', fontWeight: 600, textAlign: 'center', marginBottom: 8 }}>{scannerError}</Typography>
              <Typography variant="body2" style={{ color: '#94a3b8', textAlign: 'center', marginBottom: 20 }}>
                Please allow camera access in your browser settings and refresh the page.
              </Typography>
              <Button variant="contained" color="primary" onClick={() => window.location.reload()}
                style={{ borderRadius: 8, textTransform: 'none', fontWeight: 600 }}>
                Refresh Page
              </Button>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" alignItems="center">
              <Typography variant="body2" style={{ color: '#94a3b8', marginBottom: 12, textAlign: 'center' }}>
                Position the barcode within the blue frame to scan
              </Typography>
              <div className={classes.scannerVideoBox} style={{ height: isMobile ? '55vh' : 320 }}>
                <video ref={videoRef}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  autoPlay playsInline />
                <div className={classes.scannerOverlay} />
                {/* corner accents */}
                {[
                  { top: 12, left: 12, borderTop: '3px solid #38bdf8', borderLeft: '3px solid #38bdf8' },
                  { top: 12, right: 12, borderTop: '3px solid #38bdf8', borderRight: '3px solid #38bdf8' },
                  { bottom: 12, left: 12, borderBottom: '3px solid #38bdf8', borderLeft: '3px solid #38bdf8' },
                  { bottom: 12, right: 12, borderBottom: '3px solid #38bdf8', borderRight: '3px solid #38bdf8' },
                ].map((s, i) => (
                  <div key={i} style={{ position: 'absolute', width: 20, height: 20, borderRadius: 2, ...s }} />
                ))}
              </div>
            </Box>
          )}
        </DialogContent>

        <DialogActions style={{ background: '#0f172a', padding: '12px 20px' }}>
          <Button onClick={stopScanner} style={{
            borderRadius: 8, textTransform: 'none', fontWeight: 600,
            color: '#94a3b8', border: '1px solid #334155', padding: '6px 20px',
          }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
    </div>
  );
};

// ─── Export ───────────────────────────────────────────────────────────────────

export const SalesPackingEdit = (props: any) => (
  <Edit {...props} title="Edit Sales Packing" actions={false}>
    <SalesPackingEditForm {...props} />
  </Edit>
);